import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

export interface CreateLinkSessionParams {
  optionalDisplayName?: string;
  accountId?: string;
}

export interface CreateLinkSessionResponse {
  data: {
    sessionId: string;
    accountId: string;
    qrCodeData: string | null;
    expiresAt: string;
  };
}

export interface LinkSessionStatusResponse {
  data: {
    status: string;
    accountId?: string;
    error?: string;
    qrCodeData?: string;
  };
}

export interface InstanceListItem {
  id: string;
  displayName: string;
  phone: string | null;
  jid: string | null;
  status: string;
  instanceId: string | null;
  port: number | null;
  lastSeenAt: string | null;
  isDefault?: boolean;
}

export interface ListInstancesResponse {
  data: {
    instances: InstanceListItem[];
    deviceLimit?: number;
    deviceCount?: number;
  };
}

export interface InstanceDetailResponse {
  data: {
    id: string;
    displayName: string;
    phone: string | null;
    jid: string | null;
    status: string;
    instanceId: string;
    port: number | null;
    maskedAccessToken?: string;
    webhook?: { enabled: boolean; url?: string; events?: string[] };
  };
}

@Injectable({
  providedIn: 'root',
})
export class WhatsAppDeviceService {
  private get baseUrl(): string {
    const api = (environment.API_URL || '').replace(/\/$/, '');
    if (!api) return '/api/chatvion';
    return `${api}/chatvion`;
  }

  private get tenantId(): string {
    return environment.wasapBotTenantId ?? 'demo-tenant';
  }

  private getHeaders(): HttpHeaders {
    return new HttpHeaders({
      'Content-Type': 'application/json',
    });
  }

  constructor(private http: HttpClient) {}

  createLinkSession(params?: CreateLinkSessionParams): Observable<CreateLinkSessionResponse['data']> {
    const url = `${this.baseUrl}/tenants/${this.tenantId}/whatsapp/accounts/link-session`;
    return this.http
      .post<CreateLinkSessionResponse>(url, params ?? {}, { headers: this.getHeaders() })
      .pipe(
        map((res) => res.data),
        catchError((err) => throwError(() => err))
      );
  }

  getLinkSessionStatus(sessionId: string): Observable<LinkSessionStatusResponse['data']> {
    const url = `${this.baseUrl}/tenants/${this.tenantId}/whatsapp/accounts/link-session/${sessionId}`;
    return this.http
      .get<LinkSessionStatusResponse>(url, { headers: this.getHeaders() })
      .pipe(
        map((res) => res.data),
        catchError((err) => throwError(() => err))
      );
  }

  cancelLinkSession(sessionId: string): Observable<{ ok: boolean }> {
    const url = `${this.baseUrl}/tenants/${this.tenantId}/whatsapp/accounts/link-session/${sessionId}/cancel`;
    return this.http
      .post<{ ok: boolean }>(url, {}, { headers: this.getHeaders() })
      .pipe(catchError((err) => throwError(() => err)));
  }

  listInstances(): Observable<ListInstancesResponse['data']> {
    const url = `${this.baseUrl}/tenants/${this.tenantId}/whatsapp/instances`;
    const empty: ListInstancesResponse['data'] = { instances: [], deviceCount: 0, deviceLimit: 1 };
    return this.http
      .get<ListInstancesResponse>(url, { headers: this.getHeaders() })
      .pipe(
        map((res) => res?.data ?? empty),
        catchError((err) => {
          if (err?.status === 204 || err?.status === 0) return of(empty);
          return throwError(() => err);
        })
      );
  }

  getInstanceDetail(accountId: string): Observable<InstanceDetailResponse['data']> {
    const url = `${this.baseUrl}/tenants/${this.tenantId}/whatsapp/instances/${accountId}`;
    return this.http
      .get<InstanceDetailResponse>(url, { headers: this.getHeaders() })
      .pipe(
        map((res) => res.data),
        catchError((err) => throwError(() => err))
      );
  }

  logoutInstance(accountId: string): Observable<{ ok?: boolean; status?: string }> {
    const url = `${this.baseUrl}/tenants/${this.tenantId}/whatsapp/instances/${accountId}/logout`;
    return this.http
      .post<{ ok?: boolean; status?: string }>(url, {}, { headers: this.getHeaders() })
      .pipe(catchError((err) => throwError(() => err)));
  }

  /** Uses POST …/delete on Skiddo API (forwards DELETE to Chatvion). Browser DELETE is often blocked by IIS WebDAV (403 + broken CORS). */
  deleteInstance(accountId: string): Observable<unknown> {
    const url = `${this.baseUrl}/tenants/${this.tenantId}/whatsapp/instances/${accountId}/delete`;
    return this.http
      .post(url, {}, { headers: this.getHeaders() })
      .pipe(catchError((err) => throwError(() => err)));
  }
}
